import templateUrl from './employment-processing-dashboard.html'
import { IQService, ILogService } from 'angular'
import { EmploymentProcessingService } from '../../../core/services/data-services/employment-processing.service'
import { UtilityService } from '../../../core/services/utility.service'
import { getExceptionMessage } from '../../../core/data-utils'
import { NewHiresProcessor } from '../new-hires-processor.service'
import { NewHireVM, TerminationVM } from '../employment-processing-models'
import { TerminationsProcessor } from '../terminations-processor.service'

export const EmploymentProcessingDashboardComponent = {
    templateUrl,
    controllerAs: "vm",
    controller: class EmploymentProcessingDashboard {
        loaded: boolean
        newHiresPageNumber: number
        newHiresPageSize: number
        newHiresXlsxUrl: string
        showNewHiresSSN: boolean
        terminationsXlsxUrl: string
        newHires: NewHireVM[]
        terminations: any[]
        processedNewHires: any
        terminationsFiles: any
        terminationsFile: any
        terminatedEmployees: TerminationVM[];
        /**
         * Create a new EmploymentProcessingDashboard controller.
         * @param $q An instance of the Angular $q service.
         * @param apexApiUrl The root URL for the Apex API.
         * @param employmentProcessingService
         * And instance of the Employment Processing data service.
         * @param utilityService A service providing common utilities.
         */
        constructor(private $q: IQService, private $log: ILogService, apexApiUrl: string,
            private employmentProcessingService: EmploymentProcessingService,
            private newHiresProcessor: NewHiresProcessor,
            private terminationsProcessor: TerminationsProcessor,
            private utilityService: UtilityService) {
            'ngInject'
            this.loaded = false
            this.newHiresPageNumber = 1; // The page number for the pagination.
            this.newHiresPageSize = 10; // The page size for the pagination.
            this.newHiresXlsxUrl = apexApiUrl + "employments/new-hires.xlsx";
            this.showNewHiresSSN = false;
            this.terminationsXlsxUrl = apexApiUrl + "employments/terminations.xlsx";
        }

        /**
         * Initialize the component.
         */
        $onInit() {
            this.utilityService.showLoading();
            this.loadInitialData()
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.loaded = true;
                    this.utilityService.hideLoading();
                });
        }

        revealNewHiresSSN(v, c, i) {
            return i > 4
        }

        /**
         * Handle processing all new hires who have been selected for processing.
         */
        onProcessAllNewHires() {
            const hires = this.newHires.filter(e => canProcessNewHire(e) && e.isSelected);
            // Minimize the number of simulataneous requests by capping off a chunk of batch updates.
            const maxChunkSize = 20;
            const result = {
                succeeded: 0,
                failed: 0,
                totalCount: hires.length,
                messages: [],
                promises: []
            };
            let chunkPromise = this.$q.resolve();
            for (let i = 0; i < hires.length; i += maxChunkSize) {
                const chunkHires = hires.slice(i, i + maxChunkSize);
                chunkPromise = chunkPromise.then(() => this.newHiresProcessor.batchProcessNewHires(chunkHires))
                    .then(chunkResult => {
                        result.succeeded += chunkResult.succeeded;
                        result.failed += chunkResult.failed;
                        result.messages.push(...chunkResult.messages);
                    });
            }

            const allSaved = chunkPromise.then(() => {
                if (result.failed) {
                    const count = result.totalCount;
                    alert(`There was an error processing the new hires. ${result.succeeded}/${count}
                        succeeded, ${result.failed}/${count} failed`);
                    this.$log.warn("New hires processing failed.", result.messages);
                    return;
                }
                alert("All selected new hires were processed successfully");
            })
                .catch(ex => {
                    // Note, this isn't the usual case where a single update failed.
                    this.$log.error(ex);
                    alert("Saving failed: " + getExceptionMessage(ex))
                });

            this.utilityService.showLoadingUntilResolved(allSaved);
        }

        /**
         * Return whether any new hires can be processed and have been selected for processing.
         * @returns {boolean} Whether any new hires can be processed.
         */
        canProcessAnyNewHires() {
            return this.newHires.filter(newHire => {
                return canProcessNewHire(newHire) && newHire.isSelected;
            }).length > 0;
        }

        selectedNewHiresCount() {
            return this.newHires.reduce(function (count, newHire) {
                return count + (newHire.isSelected ? 1 : 0)
            }, 0);
        }

        /**
         * Perform the initial data load of new hires and terminations, and resolve once these
         * collections have been loaded into the controller.
         * @returns {Promise} When the data loading has resolved.
         */
        loadInitialData() {
            const newHires = this.getNewHires();
            const terminations = this.$q.when([]);// getTerminations();

            return this.$q.all([newHires, terminations])
                .then(v => {
                    this.newHires = v[0];
                    this.terminations = v[1];
                });
        }

        /**
         * Retrieve the collection of new hires and transform them into a view model.
         * @returns {Promise<{EmployeeID: number, FirstName: string, LastName: string, SSN: string, FEIN: string}>} The new hires.
         */
        getNewHires() {
            return this.employmentProcessingService.retrieveNewHires()
                .then(function (newHires) {
                    return newHires.map(function mapNewHireToViewModel(newHire) {
                        const bankInfo = newHire.Employee.BankInfo;
                        return {
                            EmployeeID: newHire.EmployeeID,
                            FirstName: newHire.Employee.FirstName,
                            LastName: newHire.Employee.LastName,
                            SSN: bankInfo ? bankInfo.SSN : null,
                            FEIN: bankInfo ? bankInfo.FEIN : null
                        };
                    });
                })
                .then(function (newHires) {
                    return newHires.sort(sortLastFirstInvariant);
                });
        }

        /**
         * Retrieve the collection of terminations and transform them into a view model.
         * @returns {Promise<{EmployeeID: number, FirstName: string, LastName: string}>} The terminations.
         */
        getTerminations() {
            return this.employmentProcessingService.retrieveTerminations()
                .then(function (terminations) {
                    return terminations.map(function (termination) {
                        return {
                            EmployeeID: termination.EmployeeID,
                            FirstName: termination.Employee.FirstName,
                            LastName: termination.Employee.LastName
                        };
                    });
                })
                .then(function (terminations) {
                    return terminations.sort(sortLastFirstInvariant);
                });
        }

        /**
         * Parse the file bound to the file input and use the resolved data to update
         * fields in the corresponding new hire.
         */
        parseProcessedNewHires() {
            if (!this.processedNewHires) {
                return;
            }

            const file = this.processedNewHires;

            // Parse the given file into a JSON array and then apply the data as a patch to the new hries.
            this.utilityService.showLoading();
            this.employmentProcessingService.parseNewHiresDataFromFile(file)
                .then(newHiresData => {
                    return newHiresData.map(this.patchNewHireFromData.bind(this));
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Parsing the file failed. Make sure that the file is in CSV format and contains all required fields. "
                        + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => this.utilityService.hideLoading());
        }

        /**
         * Find the new hire represented by the given new hire data and patch it
         * using the updated data.
         * @param {any} newHireData The patch to be applied to the new hire.
         * @returns {any} The updated new hire.
         */
        patchNewHireFromData(newHireData) {
            const newHire = this.newHires.filter(function (hire) {
                return hire.EmployeeID === Number(newHireData.EmployeeID);
            })[0];

            if (newHire) {
                newHire.ADPEmployeeID = newHireData.ADPEmployeeID ? Number(newHireData.ADPEmployeeID) : null;
                newHire.SSN = newHireData.SSN;
                newHire.FEIN = newHireData.FEIN;
                // Automatically select for processing.
                newHire.isSelected = canProcessNewHire(newHire);

                return newHire;
            }
        }

        uploadTerminations() {
            if (!this.terminationsFiles) return;

            const file = this.terminationsFiles;
            if (file) {
                this.terminationsFile = file;
                this.terminatedEmployees = null;
                this.utilityService.showLoading();
                this.employmentProcessingService.parseTerminationsDataFromFile(file)
                    .then(terminations => {
                        for (let i = 0; i < terminations.length; i++) {
                            terminations[i].isSelected = terminations[i].Selected;
                        }
                        this.terminatedEmployees = terminations;
                    })
                    .catch(ex => {
                        this.terminationsFile = null;
                        alert("Reading the file failed. Please make sure it is in the correct format.");
                    })
                    .then(() => {
                        this.utilityService.hideLoading();
                    });
            }
        }

        cancelTerminationsImport() {
            this.terminationsFiles = null;
            this.terminationsFile = null;
            this.terminatedEmployees = null;
        }

        finishTerminationsImport() {
            const terminations = this.terminatedEmployees.filter(function (employee) {
                return employee.isSelected;
            });
            // Minimize the number of simulataneous requests by capping off a chunk of batch updates.
            const maxChunkSize = 20;
            const result = {
                succeeded: 0,
                failed: 0,
                totalCount: terminations.length,
                messages: [],
                promises: []
            };
            let chunkPromise = this.$q.resolve();
            for (let i = 0; i < terminations.length; i += maxChunkSize) {
                const chunkTerminations = terminations.slice(i, i + maxChunkSize);
                chunkPromise = chunkPromise
                    .then(() => this.terminationsProcessor.batchProcessTermination(chunkTerminations))
                    .then(chunkResult => {
                        result.succeeded += chunkResult.succeeded;
                        result.failed += chunkResult.failed;
                        result.messages.push(...chunkResult.messages);
                    });
            }

            const allSaved = chunkPromise.then(() => {
                if (result.failed) {
                    const count = result.totalCount;
                    alert(`There was an error processing the terminations. ${result.succeeded}/${count}
                        succeeded, ${result.failed}/${count} failed`);
                    this.$log.warn("Terminations processing failed.", result.messages);
                    return;
                }
                alert("All selected terminations were processed successfully");
            })
                .catch(ex => {
                    // Note, this isn't the usual case where a single update failed.
                    this.$log.error(ex);
                    alert("Saving failed: " + getExceptionMessage(ex))
                });
            this.utilityService.showLoadingUntilResolved(allSaved);
        }
    }
}


/**
 * Return whether the given new hire can be processed.
 * @param {any} newHire The new hire.
 * @returns {boolean} Whether the new hire can be processed.
 */
function canProcessNewHire(newHire) {
    return newHire.ADPEmployeeID !== null && newHire.ADPEmployeeID !== undefined;
}


/**
 * Sort an employment by last name, then first name, ignoring case.
 * @param {{ FirstName: string, LastName: string}} a The left comparison object.
 * @param {{ FirstName: string, LastName: string}} b The right comparison object
 * @returns {Number} The sort direction.
 */
function sortLastFirstInvariant(a, b) {
    if ((a.LastName || "").toLowerCase() < (b.LastName || "").toLowerCase()) {
        return -1;
    } else if ((a.LastName || "").toLowerCase() > (b.LastName || "").toLowerCase()) {
        return 1;
    }
    if ((a.FirstName || "").toLowerCase() < (b.FirstName || "").toLowerCase()) {
        return -1;
    }
    return 1;
}
